/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Tag,
  Text,
  Th,
  Thead,
  Tr,
  HStack,
  Tooltip,
  useDisclosure,
  useToast,
  useColorModeValue,
  Select,
  Flex,
  IconButton,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FiList, FiRefreshCw, FiUpload, FiX, FiTrash2, FiDownload } from "react-icons/fi";
import api, { apiFetch } from "utils/api";
import SimStatusBadge from "components/SimManagement/SimStatusBadge";
import SimBulkUploadModal from "components/Modals/Sim/SimBulkUploadModal";
import SimNotlarModal from "components/Modals/Sim/SimNotlarModal";
import SimIdentifiersEditModal from "components/Modals/Sim/SimIdentifiersEditModal";
import SimContextMenu from "components/ContextMenu/SimContextMenu";
import FilterPanel from "components/FilterPanel/FilterPanel";
import { useBayiOptions } from "hooks/useBayiOptions";
import { useUserRole } from "hooks/useUserRole";
import { useUser } from "contexts/UserContext";
import { usePageVisibility } from "hooks/usePageVisibility";
import { useErrorHandler } from "hooks/useErrorHandler";
import { getErrorMessage } from "utils/errorHandler";
import SplashScreen from "components/SplashScreen/SplashScreen";

export default function SimYonetimi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const [selectedDate, setSelectedDate] = useState("");
  const [sims, setSims] = useState([]);
  const userRole = useUserRole();
  const { userBayiTipi } = useUser();
  const isPageVisible = usePageVisibility(); // Page Visibility API
  
  // Bayiler hook'u sadece admin ve teknik_eleman için aktif (yetkili bayi ve saas bayi göremez)
  const shouldLoadBayiler = userRole === "admin" || userRole === "teknik_eleman";
  const {
    bayiler,
    loading: bayilerLoading,
  } = useBayiOptions(shouldLoadBayiler);
  
  // Filtre state (kullanıcının yazdığı/seçtiği değerler)
  const [iccidFilter, setIccidFilter] = useState("");
  const [imsiFilter, setImsiFilter] = useState("");
  const [telefonFilter, setTelefonFilter] = useState("");
  const [bayiFilter, setBayiFilter] = useState("");
  const [durumFilter, setDurumFilter] = useState("");
  
  // Uygulanan filtreler (Listele butonuna basınca bu değerler kullanılacak)
  const [appliedIccidFilter, setAppliedIccidFilter] = useState("");
  const [appliedImsiFilter, setAppliedImsiFilter] = useState("");
  const [appliedTelefonFilter, setAppliedTelefonFilter] = useState("");
  const [appliedDurumFilter, setAppliedDurumFilter] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [appliedBayiFilter, setAppliedBayiFilter] = useState(""); // Backend'de filtreleniyor, frontend'de kullanılmıyor
  
  // Arama yapılıp yapılmadığını takip et
  const [hasSearched, setHasSearched] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notesModalSim, setNotesModalSim] = useState(null);
  const [editIdentifiersSim, setEditIdentifiersSim] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [scanLoading, setScanLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [operations, setOperations] = useState([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState(null);
  const [showCompletedOperations, setShowCompletedOperations] = useState(false);
  const [cancellingIds, setCancellingIds] = useState(new Set());
  const [checkingIds, setCheckingIds] = useState(new Set());
  const [sendingInfoIds, setSendingInfoIds] = useState(new Set());
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [contextMenu, setContextMenu] = useState({ position: null, sim: null });
  const [sortByStatus, setSortByStatus] = useState(false);
  const bulkDisclosure = useDisclosure();
  const operationsDisclosure = useDisclosure();
  const panelBg = useColorModeValue("white", "gray.800");
  const panelBorder = useColorModeValue("gray.100", "whiteAlpha.200");

  // userBayiTipi artık Context'ten geliyor, ayrı API çağrısına gerek yok
  
  // Operasyonları yenile (modal açmadan) - useEffect'lerden önce tanımlanmalı
  const refreshOperations = useCallback(async () => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    try {
      setOperationsError(null);
      const res = await api.get("/api/sim-kartlari/operations");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SIM operasyonları alınamadı.");
      }
      setOperations(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = getErrorMessage(err, "SIM operasyonları yenileme hatası");
      setOperationsError(errorMessage);
      // Sessizce hata kaydet, toast gösterme (modal açıkken rahatsız etmesin)
    }
  }, []);
  
  // Modal açıkken otomatik yenileme için interval
  // Page Visibility API ile optimize edildi: Sayfa görünür değilken polling durdurulur
  useEffect(() => {
    let intervalId = null;
    
    if (operationsDisclosure.isOpen && isPageVisible) {
      // Modal açıkken ve sayfa görünürken her 5 saniyede bir operasyonları yenile
      intervalId = setInterval(() => {
        refreshOperations();
      }, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [operationsDisclosure.isOpen, isPageVisible, refreshOperations]);
  
  // SIM operasyonu başlatıldığında event dinle (Araclar.js'den gelen)
  useEffect(() => {
    const handleSimOperationStarted = () => {
      // Modal açıksa listeyi yenile
      if (operationsDisclosure.isOpen) {
        // Kısa bir gecikme ile yenile (backend'in state'i güncellemesi için)
        setTimeout(() => {
          refreshOperations();
        }, 1000);
      }
    };
    
    window.addEventListener('simOperationStarted', handleSimOperationStarted);
    
    return () => {
      window.removeEventListener('simOperationStarted', handleSimOperationStarted);
    };
  }, [operationsDisclosure.isOpen, refreshOperations]);

  const fetchSims = useCallback(async (targetDate, applyFilters = false, filters = null) => {
    // Eğer filtreler uygulanacaksa (Listele butonuna basınca)
    if (applyFilters && filters) {
      setAppliedIccidFilter(filters.iccid || "");
      setAppliedImsiFilter(filters.imsi || "");
      setAppliedTelefonFilter(filters.telefon || "");
      setAppliedBayiFilter(filters.bayi || "");
      setHasSearched(true);
    }
    
    setLoading(true);
    setError(null);
    try {
      // Query parametrelerini oluştur
      const params = new URLSearchParams();
      if (targetDate) {
        params.append("date", targetDate);
      }
      
      // Bayi filtresini belirle: önce yeni filtrelerden, yoksa uygulanmış filtrelerden
      const bayiFilterToUse = (applyFilters && filters && filters.bayi) ? filters.bayi : appliedBayiFilter;
      if (bayiFilterToUse) {
        // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
        if (bayiFilterToUse === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", bayiFilterToUse);
        }
      }

      const iccidUse = applyFilters && filters ? (filters.iccid || "") : appliedIccidFilter;
      const imsiUse = applyFilters && filters ? (filters.imsi || "") : appliedImsiFilter;
      const telefonUse = applyFilters && filters ? (filters.telefon || "") : appliedTelefonFilter;
      const durumUse = applyFilters && filters ? (filters.durum || "") : appliedDurumFilter;
      if (iccidUse?.trim()) params.append("iccid", iccidUse.trim());
      if (imsiUse?.trim()) params.append("imsi", imsiUse.trim());
      if (telefonUse?.trim()) params.append("telefon", telefonUse.trim());
      if (durumUse?.trim()) params.append("durum", durumUse.trim());
      
      // URL oluştur: Eğer params varsa ? ile başlamalı, yoksa boş string
      const finalUrl = params.toString() ? `/api/sim-kartlari/?${params.toString()}` : `/api/sim-kartlari/`;
      const res = await api.get(finalUrl);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SIM listesi alınamadı.");
      }
      setSims(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "SIM listesi yükleme hatası");
      setError(errorMessage);
      setSims([]);
    } finally {
      setLoading(false);
    }
  }, [
    appliedBayiFilter,
    appliedIccidFilter,
    appliedImsiFilter,
    appliedTelefonFilter,
    appliedDurumFilter,
    handleError,
  ]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const res = await api.get("/api/sim-kartlari/dates");
      const data = await res.json();
      if (Array.isArray(data)) {
        setAvailableDates(data);
        // Otomatik tarih seçimini kaldırdık - kullanıcı manuel seçmeli
      } else {
        setAvailableDates([]);
      }
    } catch (err) {
      // Merkezi error handling - Sessizce hata kaydet (kritik değil)
      // SADECE development'ta console'a yaz
      if (process.env.NODE_ENV === 'development') {
        console.error('[SimYonetimi] Tarih listesi yükleme hatası:', err);
      }
      setAvailableDates([]);
    }
  }, []);

  // Tarih değiştiğinde otomatik yükleme yapma, sadece kullanıcı "Listele" butonuna bastığında yükle
  // useEffect kaldırıldı - artık otomatik yükleme yok

  useEffect(() => {
    fetchAvailableDates();
  }, [fetchAvailableDates]);

  const visibleOperations = useMemo(() => {
    // completed değerini boolean'a çevir (güvenli kontrol)
    const normalizeCompleted = (val) => {
      if (val === true || val === "true" || val === 1 || val === "1") return true;
      return false;
    };
    
    if (showCompletedOperations) {
      // Sadece tamamlanan işlemleri göster
      return operations.filter((op) => normalizeCompleted(op.completed) === true);
    }
    // Sadece aktif (tamamlanmamış) işlemleri göster
    return operations.filter((op) => normalizeCompleted(op.completed) === false);
  }, [operations, showCompletedOperations]);

  const completedOperationsCount = useMemo(
    () => operations.filter((op) => op.completed).length,
    [operations]
  );

  const hasCancelableOperation = useMemo(
    () =>
      operations.some(
        (op) =>
          !op.completed &&
          op.status_code !== "cancelled" &&
          op.status_code !== "number_gathered" &&
          op.status_code !== "timeout"
      ),
    [operations]
  );

  const completedToggleLabel = useMemo(() => {
    if (showCompletedOperations) {
      return "Tamamlananları Gizle";
    }
    const suffix = completedOperationsCount ? ` (${completedOperationsCount})` : "";
    return `Tamamlananları Göster${suffix}`;
  }, [showCompletedOperations, completedOperationsCount]);

  const filteredSims = useMemo(() => {
    let list = [...sims];
    if (sortByStatus) {
      list.sort((a, b) => {
        const aIsKullanimda = a.durum === "kullanimda";
        const bIsKullanimda = b.durum === "kullanimda";
        if (aIsKullanimda && !bIsKullanimda) return -1;
        if (!aIsKullanimda && bIsKullanimda) return 1;
        return 0;
      });
    }
    return list;
  }, [sims, sortByStatus]);

  // Pagination hesaplamaları
  const totalPages = Math.ceil(filteredSims.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSims = filteredSims.slice(startIndex, endIndex);

  // SIM istatistikleri
  const simStats = useMemo(() => {
    const total = filteredSims.length;
    const kullanimda = filteredSims.filter((sim) => sim.durum === "kullanimda").length;
    const hazir = filteredSims.filter((sim) => sim.durum === "hazir").length;
    const bosaCikan = filteredSims.filter((sim) => sim.durum === "bosa_cikan").length;
    return { total, kullanimda, hazir, bosaCikan };
  }, [filteredSims]);

  // Sayfa değiştiğinde veya filtreler değiştiğinde ilk sayfaya dön
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedIccidFilter, appliedImsiFilter, appliedTelefonFilter, appliedDurumFilter, appliedBayiFilter, selectedDate]);

  const escapeRegExp = (value) =>
    value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Cache regex patterns to avoid recreating on every render
  const regexCache = useRef(new Map());
  
  const highlightMatch = useCallback((value, query) => {
    if (!value) return "-";
    if (!query) return value;
    
    // Use cached regex if available
    let regex = regexCache.current.get(query);
    if (!regex) {
      regex = new RegExp(`(${escapeRegExp(query)})`, "gi");
      regexCache.current.set(query, regex);
    }
    
    return value.split(regex).map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <Box as="span" key={index} bg="yellow.200" px={0.5} py={0.2} borderRadius="xs">
          {part}
        </Box>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  }, []);
  
  // Tüm filtreleri birleştir (highlight için)
  const combinedSearchQuery = useMemo(() => {
    return [appliedIccidFilter, appliedImsiFilter, appliedTelefonFilter].filter(Boolean).join(" ");
  }, [appliedIccidFilter, appliedImsiFilter, appliedTelefonFilter]);

  // eslint-disable-next-line no-unused-vars
  const handleUsageScan = async (path = "/api/sim-kartlari/usage-scan") => {
    try {
      setScanLoading(true);
      const res = await api.post(path, {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Kontrol tamamlanamadı.");
      }
      toast({
        title: data.message || "Durum kontrolü tamamlandı",
        description: `${data.marked_in_use} yeni kullanım, ${data.marked_idle} pasif.`,
        status: "success",
      });
      // Sadece eğer daha önce bir arama yapıldıysa veya tarih seçiliyse yeniden yükle
      if (selectedDate || hasSearched) {
        // "all" seçildiğinde boş string gönder (tüm tarihler)
        fetchSims(selectedDate === "all" ? "" : (selectedDate || ""), false, null);
      }
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM kullanım taraması hatası");
    } finally {
      setScanLoading(false);
    }
  };

  const handleDataMutation = () => {
    // Sadece eğer daha önce bir arama yapıldıysa veya tarih seçiliyse yeniden yükle
    if (selectedDate || hasSearched) {
      // "all" seçildiğinde boş string gönder (tüm tarihler)
      fetchSims(selectedDate === "all" ? "" : (selectedDate || ""), false, null);
    }
    fetchAvailableDates();
  };

  const statusColorMap = {
    waiting_signal: "yellow",
    waiting_q: "orange",
    waiting_c: "orange",
    command_sent: "blue",
    inventory_missing: "red",
    identifier_missing: "orange",
    number_gathered: "green",
    already_assigned: "teal",
    protocol_missing: "gray",
    error: "red",
    idle: "gray",
    cancelled: "gray",
    checking_logs: "blue",
    checking_device: "blue",
    command_sending: "blue",
    starting: "blue",
    timeout: "orange",
  };

  const statusLabelMap = {
    waiting_signal: "Sinyal Bekleniyor",
    waiting_q: "Komut Bekleniyor",
    waiting_c: "Komut Bekleniyor",
    command_sent: "Komut Gönderildi",
    inventory_missing: "Envanter Eksik",
    identifier_missing: "Tanımlayıcı Eksik",
    number_gathered: "Numara Toplandı",
    already_assigned: "Zaten Atanmış",
    protocol_missing: "Protokol Eksik",
    error: "Hata",
    idle: "Beklemede",
    cancelled: "İptal Edildi",
    checking_logs: "Ham loglar ve cihaz yanıtları izleniyor",
    checking_device:
      "Traccar cihaz kaydından SIM bilgisi okunuyor (sunucu kaydı, ham log değil)",
    command_sending: "Komut Gönderiliyor",
    starting: "Başlatılıyor",
    timeout: "Zaman Aşımı",
  };

  const getStatusColor = (code) => statusColorMap[code] || "gray";
  const getStatusLabel = (code) => {
    if (!code) return "Bilinmeyen";
    // Eğer code statusLabelMap'te varsa Türkçe etiketi döndür
    if (statusLabelMap[code]) return statusLabelMap[code];
    // Eğer code İngilizce bir kelime içeriyorsa Türkçe'ye çevir
    const codeLower = code.toLowerCase();
    if (codeLower.includes("starting")) return "Başlatılıyor";
    if (codeLower.includes("waiting")) return "Bekleniyor";
    if (codeLower.includes("error")) return "Hata";
    if (codeLower.includes("completed")) return "Tamamlandı";
    if (codeLower.includes("failed")) return "Başarısız";
    // Hiçbiri değilse code'u olduğu gibi döndür
    return code;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("tr-TR");
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    
    // Backend'den gelen timestamp'ler UTC olarak geliyor
    // Frontend'de timezone conversion YAPMIYORUZ - backend'den gelen değeri direkt gösteriyoruz
    // Veritabanında UTC olarak saklanan değer, backend'den UTC olarak geliyor, frontend'de de UTC olarak gösteriyoruz
    let dt;
    if (typeof value === "string") {
      // ISO formatında timestamp'i parse et
      if (value.includes("T")) {
        if (value.includes("+") || value.includes("Z")) {
          // Timezone-aware timestamp'i parse et
          dt = new Date(value);
        } else {
          // Timezone bilgisi yok, UTC olarak ekle
          dt = new Date(value + "Z");
        }
      } else {
        dt = new Date(value);
      }
    } else {
      dt = new Date(value);
    }
    
    if (Number.isNaN(dt.getTime())) return value;
    
    // UTC olarak formatla - timezone conversion YOK
    // getUTCFullYear, getUTCMonth, getUTCDate, getUTCHours, getUTCMinutes, getUTCSeconds kullanarak
    // UTC zamanını direkt gösteriyoruz
    const year = dt.getUTCFullYear();
    const month = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dt.getUTCDate()).padStart(2, '0');
    const hours = String(dt.getUTCHours()).padStart(2, '0');
    const minutes = String(dt.getUTCMinutes()).padStart(2, '0');
    const seconds = String(dt.getUTCSeconds()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };


  // Modal aç ve operasyonları yükle
  const handleOpenOperations = async () => {
    try {
      setOperationsLoading(true);
      setOperationsError(null);
      // Modal açılırken showCompletedOperations'ı false yap (sadece aktif operasyonları göster)
      setShowCompletedOperations(false);
      const res = await api.get("/api/sim-kartlari/operations");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SIM operasyonları alınamadı.");
      }
      setOperations(Array.isArray(data) ? data : []);
      operationsDisclosure.onOpen();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "SIM operasyonları yükleme hatası");
      setOperationsError(errorMessage);
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleCancelOperation = async (deviceId) => {
    try {
      setCancellingIds((prev) => new Set(prev).add(deviceId));
      const res = await api.post(`/api/sim-kartlari/gather/${deviceId}/cancel`, {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "İptal işlemi başarısız.");
      }
      toast({
        title: "Başarılı",
        description: data.message || "İşlem iptal edildi",
        status: "success",
      });
      // Modal açıksa sadece listeyi yenile, modalı tekrar açma
      if (operationsDisclosure.isOpen) {
        await refreshOperations();
      } else {
        await handleOpenOperations();
      }
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM kullanım taraması hatası");
    } finally {
      setCancellingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
  };

  const handleCheckNow = async (deviceId) => {
    try {
      setCheckingIds((prev) => new Set(prev).add(deviceId));
      const res = await api.post(`/api/sim-kartlari/gather/${deviceId}/check-now`, {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || "Kontrol işlemi başarısız.");
      }
      toast({
        title: data.status === "success" ? "Başarılı" : "Bilgi",
        description: data.message || "Loglar kontrol edildi",
        status: data.status === "success" ? "success" : "info",
        duration: 4000,
      });
      // Modal açıksa sadece listeyi yenile, modalı tekrar açma
      if (operationsDisclosure.isOpen) {
        await refreshOperations();
      } else {
        await handleOpenOperations();
      }
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM kullanım taraması hatası");
    } finally {
      setCheckingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
  };

  const handleSendTeltonikaInfo = async (deviceId) => {
    try {
      setSendingInfoIds((prev) => new Set(prev).add(deviceId));
      const res = await api.post(`/api/sim-kartlari/send-teltonika-info/${deviceId}`, {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || "Komut gönderilemedi.");
      }
      toast({
        title: "Başarılı",
        description: data.message || "Bilgi alma komutu gönderildi",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM kullanım taraması hatası");
    } finally {
      setSendingInfoIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
  };

  /** GT06 / GS900: Traccar custom ICCID komutu (Teltonika getinfo’a benzer) */
  const handleSendGt06Iccid = async (deviceId) => {
    try {
      setSendingInfoIds((prev) => new Set(prev).add(deviceId));
      const res = await api.post(`/api/sim-kartlari/send-gt06-iccid/${deviceId}`, {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || data?.message || "Komut gönderilemedi.");
      }
      toast({
        title: "Başarılı",
        description: data.message || "ICCID komutu gönderildi",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      handleError(err, "GT06 ICCID komutu hatası");
    } finally {
      setSendingInfoIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deviceId);
        return newSet;
      });
    }
  };

  const handleClearOldOperations = async () => {
    if (!window.confirm("Tamamlanmış eski operasyonlar silinsin mi?")) return;
    try {
      setOperationsLoading(true);
      // Body göndermeden POST isteği yap
      const res = await apiFetch("/api/sim-kartlari/gather/clear-completed", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        // JSON parse hatası - response text'i kullan
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}: ${res.statusText}`);
      }
      
      if (!res.ok) {
        const errorMsg = data?.detail || data?.message || (typeof data === 'string' ? data : JSON.stringify(data)) || `HTTP ${res.status}: ${res.statusText}`;
        throw new Error(errorMsg);
      }
      
      toast({
        title: "Tamamlandı",
        description: data.message || `${data.cleared || 0} tamamlanmış operasyon silindi`,
        status: "success",
      });
      // Modal açıksa sadece listeyi yenile, modalı tekrar açma
      if (operationsDisclosure.isOpen) {
        await refreshOperations();
      } else {
        await handleOpenOperations();
      }
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM operasyonları temizleme hatası");
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setPdfLoading(true);
      
      // Query parametrelerini oluştur (uygulanan filtreleri kullan)
      const params = new URLSearchParams();
      // "all" seçildiğinde tarih parametresi gönderme (tüm tarihler)
      if (selectedDate && selectedDate !== "all") {
        params.append("date", selectedDate);
      }
      if (appliedBayiFilter) {
        // "ixpilot" seçildiğinde özel bir değer gönder (backend'de null olarak yorumlanacak)
        if (appliedBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", appliedBayiFilter);
        }
      }
      if (appliedIccidFilter) {
        params.append("iccid", appliedIccidFilter);
      }
      if (appliedImsiFilter) {
        params.append("imsi", appliedImsiFilter);
      }
      if (appliedTelefonFilter) {
        params.append("telefon", appliedTelefonFilter);
      }
      if (appliedDurumFilter) {
        params.append("durum", appliedDurumFilter);
      }
      
      const queryString = params.toString();
      const url = `/api/sim-kartlari/pdf${queryString ? `?${queryString}` : ""}`;
      
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, {
        method: "GET",
      });
      if (!response.ok) {
        throw new Error("PDF oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      // Dosya adını filtrelerle oluştur
      let filename = "sim_raporu.pdf";
      const filenameParts = [];
      // "all" seçildiğinde dosya adına ekleme (tüm tarihler)
      if (selectedDate && selectedDate !== "all") {
        filenameParts.push(selectedDate);
      }
      if (appliedBayiFilter && userRole === "admin") {
        const selectedBayi = bayiler.find(b => b.id === appliedBayiFilter);
        if (selectedBayi) {
          filenameParts.push(selectedBayi.name.replace(/\s+/g, "_"));
        }
      }
      if (filenameParts.length > 0) {
        filename = `sim_raporu_${filenameParts.join("_")}.pdf`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      toast({
        title: "PDF indirildi",
        description: "SIM raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(error, "PDF indirme hatası");
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setExcelLoading(true);

      const params = new URLSearchParams();
      if (selectedDate && selectedDate !== "all") {
        params.append("date", selectedDate);
      }
      if (appliedBayiFilter) {
        if (appliedBayiFilter === "ixpilot") {
          params.append("bayi_id", "null");
        } else {
          params.append("bayi_id", appliedBayiFilter);
        }
      }
      if (appliedIccidFilter) {
        params.append("iccid", appliedIccidFilter);
      }
      if (appliedImsiFilter) {
        params.append("imsi", appliedImsiFilter);
      }
      if (appliedTelefonFilter) {
        params.append("telefon", appliedTelefonFilter);
      }
      if (appliedDurumFilter) {
        params.append("durum", appliedDurumFilter);
      }

      const queryString = params.toString();
      const url = `/api/sim-kartlari/excel${queryString ? `?${queryString}` : ""}`;
      const { apiFetch } = await import("utils/api");
      const response = await apiFetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Excel oluşturulamadı");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      let filename = "sim_raporu.xlsx";
      const filenameParts = [];
      if (selectedDate && selectedDate !== "all") {
        filenameParts.push(selectedDate);
      }
      if (appliedBayiFilter && userRole === "admin") {
        const selectedBayi = bayiler.find((b) => b.id === appliedBayiFilter);
        if (selectedBayi) {
          filenameParts.push(selectedBayi.name.replace(/\s+/g, "_"));
        }
      }
      if (filenameParts.length > 0) {
        filename = `sim_raporu_${filenameParts.join("_")}.xlsx`;
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Excel indirildi",
        description: "SIM Excel raporu başarıyla indirildi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      handleError(error, "Excel indirme hatası");
    } finally {
      setExcelLoading(false);
    }
  };

  const handleCancelAll = async () => {
    if (!window.confirm("Tüm aktif SIM toplama işlemlerini iptal etmek istediğinize emin misiniz?")) {
      return;
    }
    try {
      setOperationsLoading(true);
      const res = await api.post("/api/sim-kartlari/gather/cancel/all", {});
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "İptal işlemi başarısız.");
      }
      toast({
        title: "Başarılı",
        description: data.message || "Tüm işlemler iptal edildi",
        status: "success",
      });
      // Modal açıksa sadece listeyi yenile, modalı tekrar açma
      if (operationsDisclosure.isOpen) {
        await refreshOperations();
      } else {
        await handleOpenOperations();
      }
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "SIM kullanım taraması hatası");
    } finally {
      setOperationsLoading(false);
    }
  };

  const handleSimContextMenu = (event, sim) => {
    event.preventDefault();
    setContextMenu({
      position: { x: event.clientX, y: event.clientY },
      sim,
    });
  };

  const closeContextMenu = () => setContextMenu({ position: null, sim: null });

  const handleShowAllNotes = (sim) => {
    if (!sim?.id) return;
    closeContextMenu();
    setNotesModalSim(sim);
  };

  const handleEditIdentifiers = (sim) => {
    if (!sim?.id) return;
    closeContextMenu();
    setEditIdentifiersSim(sim);
  };

  const handleToggleRoaming = async (sim, enabled) => {
    if (!sim?.id) return;
    closeContextMenu();
    try {
      const res = await api.post(`/api/sim-kartlari/${sim.id}/roaming`, {
        enabled,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Roaming durumu güncellenemedi.");
      }
      setSims((prev) =>
        prev.map((item) => (item.id === sim.id ? { ...item, ...data } : item))
      );
      toast({
        title: "Roaming güncellendi",
        description: enabled ? "Roaming aktif edildi." : "Roaming pasif yapıldı.",
        status: "success",
      });
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Roaming durumu güncelleme hatası");
    }
  };

  const handleSetDurumHazir = async (sim) => {
    if (!sim?.id) return;
    closeContextMenu();
    try {
      const res = await api.post(`/api/sim-kartlari/${sim.id}/durum-hazir`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SIM durumu güncellenemedi.");
      }
      setSims((prev) =>
        prev.map((item) => (item.id === sim.id ? { ...item, ...data } : item))
      );
      toast({
        title: "Durum güncellendi",
        description: "SIM durumu 'hazir' olarak güncellendi.",
        status: "success",
      });
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Roaming durumu güncelleme hatası");
    }
  };

  const renderTable = () => {
    if (error) {
      return (
        <Center py={10}>
          <Text color="red.400">{error}</Text>
        </Center>
      );
    }

    if (!sims.length && !hasSearched) {
      return (
        <Center py={10}>
          <Text fontSize="sm" color="gray.600">
            Henüz liste yüklenmedi. Tarih seçin veya filtreleri doldurup 'Listele' butonuna basarak arama yapabilirsiniz.
          </Text>
        </Center>
      );
    }

    if (!filteredSims.length && hasSearched) {
      return (
        <Center py={10}>
          <Text fontSize="sm" color="gray.600">
            Arama kriterlerinize uygun kayıt bulunamadı.
          </Text>
        </Center>
      );
    }

    return (
      <Box overflowX="auto" position="relative">
        {/* Splash Screen - Sadece tablo alanında göster */}
        <SplashScreen 
          isLoading={loading} 
          message="SIM kartları yükleniyor..."
          fullScreen={false}
        />
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>ICC ID</Th>
              <Th>IMSI</Th>
              <Th>Telefon</Th>
              <Th>Satın Alınan</Th>
              <Th>Tespit / Atama</Th>
              <Th 
                cursor="pointer" 
                onClick={() => setSortByStatus(!sortByStatus)}
                _hover={{ bg: "gray.100" }}
                title="Kullanımda olanları üste getirmek için tıklayın"
              >
                Durum {sortByStatus && "↑"}
              </Th>
              <Th>IMEI</Th>
              <Th>Plaka</Th>
              <Th>Firma</Th>
              {userRole !== "bayi" && <Th>İş Ortağı</Th>}
              <Th>Roaming</Th>
              <Th>Notlar</Th>
            </Tr>
          </Thead>
          <Tbody>
            {paginatedSims.map((sim) => {
              const isRoamingActive = (sim.roaming || "").toLowerCase() === "aktif";
              const tespitAtama =
                sim.kullanim_raporlari && sim.kullanim_raporlari.length
                  ? sim.kullanim_raporlari[0].timestamp
                  : null;
              return (
                <Tr
                  key={sim.id}
                  onContextMenu={(event) => handleSimContextMenu(event, sim)}
                  style={{ cursor: "context-menu" }}
                >
                  <Td>{highlightMatch(sim.iccid, combinedSearchQuery)}</Td>
                  <Td>{highlightMatch(sim.imsi, combinedSearchQuery)}</Td>
                  <Td>{highlightMatch(sim.tel_no, combinedSearchQuery)}</Td>
                  <Td>{formatDate(sim.satin_alinan_tarih)}</Td>
                  <Td>
                    <Text fontSize="sm" color={tespitAtama ? "gray.700" : "gray.400"}>
                      {tespitAtama ? formatDateTime(tespitAtama) : "-"}
                    </Text>
                  </Td>
                  <Td>
                    <SimStatusBadge value={sim.durum} />
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={sim.imei ? "gray.700" : "gray.400"}>
                      {sim.imei || "-"}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={sim.plaka ? "gray.700" : "gray.400"}>
                      {sim.plaka || "-"}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={sim.firma_ismi ? "gray.700" : "gray.400"}>
                      {sim.firma_ismi || "-"}
                    </Text>
                  </Td>
                  {userRole !== "bayi" && (
                    <Td>
                      <Text fontSize="sm" color={sim.bayi_username ? "gray.700" : "gray.400"}>
                        {sim.bayi_username || "ixPilot"}
                      </Text>
                    </Td>
                  )}
                  <Td>
                    <Tag size="sm" colorScheme={isRoamingActive ? "green" : "gray"}>
                      {isRoamingActive ? "Aktif" : "Pasif"}
                    </Tag>
                  </Td>
                  <Td>
                    {(() => {
                      // sim_usage_logs tablosundan notları al (en son notları göster)
                      const notes = sim.kullanim_raporlari
                        ? sim.kullanim_raporlari
                            .filter(log => log.note && log.note.trim())
                            .map(log => log.note)
                            .slice(0, 3) // En son 3 notu göster
                        : [];
                      const notesText = notes.length > 0 ? notes.join(" | ") : null;
                      return (
                        <Tooltip label={notesText || ""} isDisabled={!notesText}>
                          <Text 
                            fontSize="sm" 
                            color={notesText ? "gray.700" : "gray.400"}
                            maxW="200px"
                            isTruncated
                          >
                            {notesText || "-"}
                          </Text>
                        </Tooltip>
                      );
                    })()}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  };

  const paginationBg = useColorModeValue("white", "gray.800");
  const paginationBorder = useColorModeValue("gray.200", "gray.700");
  const paginationText = useColorModeValue("gray.600", "gray.400");

  const renderPagination = () => {
    if (!filteredSims.length) return null;

  return (
      <Flex
        justify="space-between"
        align="center"
        mt={4}
        px={4}
        py={3}
        bg={paginationBg}
        borderRadius="lg"
        border="1px solid"
        borderColor={paginationBorder}
        flexWrap="wrap"
        gap={3}
      >
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color={paginationText}>
            Sayfa başına:
        </Text>
          <Select
                    size="sm"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            w="80px"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
        </Flex>

        <Flex align="center" gap={2}>
          <Text fontSize="sm" color={paginationText}>
            {startIndex + 1}-{Math.min(endIndex, filteredSims.length)} / {filteredSims.length}
          </Text>
        </Flex>

        <Flex align="center" gap={2}>
            <IconButton
            icon={<FiChevronLeft />}
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            isDisabled={currentPage === 1}
            aria-label="Önceki sayfa"
          />
          
          <HStack spacing={1}>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                  <Button
                  key={pageNum}
                    size="sm"
                  variant={currentPage === pageNum ? "solid" : "outline"}
                  colorScheme={currentPage === pageNum ? "blue" : "gray"}
                  onClick={() => setCurrentPage(pageNum)}
                  minW="32px"
                >
                  {pageNum}
                  </Button>
              );
            })}
        </HStack>

            <IconButton
            icon={<FiChevronRight />}
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            isDisabled={currentPage === totalPages}
            aria-label="Sonraki sayfa"
          />
        </Flex>
      </Flex>
    );
  };

  return (
    <>
    <Box position="relative" mt={{ base: 2, md: 6 }} zIndex={1}>

      <FilterPanel
        fieldWidth={{ base: "100%", md: "120px", lg: "130px" }} // Tek sıraya sığması için küçültüldü
        fields={[
          {
            key: "tarih",
            type: "select",
            placeholder: "Satın Alma Tarihi",
            value: selectedDate,
            onChange: setSelectedDate,
            options: [
              { label: "Tümü", value: "all" }, // "Tümü" için özel değer
              ...availableDates.map((date) => ({
                label: formatDate(date),
                value: date,
              })),
            ],
          },
          // Bayiler filtresi - sadece admin görebilir (teknik_eleman sim yönetimine erişemez)
          ...(userRole === "admin" ? [{
            key: "bayi",
            type: "select",
            placeholder: "Bayi Seçiniz",
            value: bayiFilter,
            onChange: setBayiFilter,
            options: [
              { label: "Tümü", value: "" },
              { label: "ixPilot", value: "ixpilot" },
              ...bayiler.map((bayi) => ({
                label: bayi.name,
                value: bayi.id,
              })),
            ],
            isDisabled: bayilerLoading,
          }] : []),
          {
            key: "iccid",
            type: "text",
            placeholder: "ICC ID",
            value: iccidFilter,
            onChange: setIccidFilter,
          },
          {
            key: "imsi",
            type: "text",
            placeholder: "IMSI",
            value: imsiFilter,
            onChange: setImsiFilter,
          },
          {
            key: "telefon",
            type: "text",
            placeholder: "Telefon",
            value: telefonFilter,
            onChange: setTelefonFilter,
          },
          {
            key: "durum",
            type: "select",
            placeholder: "Durum",
            value: durumFilter,
            onChange: setDurumFilter,
            options: [
              { label: "Tümü", value: "" },
              { label: "Hazır", value: "hazir" },
              { label: "Kullanımda", value: "kullanimda" },
              { label: "Boşa Çıkan", value: "bosa_cikan" },
            ],
          },
        ]}
        onSearch={() => {
          // Tarih seçimi özel durumu: "Tümü" seçildiğinde (value="all") bu geçerli bir filtre olarak kabul edilir
          const tarihSecildi = selectedDate && selectedDate !== "";
          
          // En az bir filtre seçilmiş olmalı (tarih seçimi "Tümü" olsa bile geçerli)
          const hasAnyFilter = 
            tarihSecildi || // Tarih seçimi yapıldıysa (Tümü dahil) geçerli
            (iccidFilter && iccidFilter.trim()) ||
            (imsiFilter && imsiFilter.trim()) ||
            (telefonFilter && telefonFilter.trim()) ||
            (durumFilter && durumFilter.trim()) ||
            (bayiFilter && bayiFilter.trim());
          
          if (!hasAnyFilter) {
            toast({
              title: "Uyarı",
              description: "Lütfen en az bir filtre seçin.",
              status: "warning",
              duration: 3000,
              isClosable: true,
            });
            return;
          }
          
          // Uygulanan filtreleri güncelle
          setAppliedIccidFilter(iccidFilter);
          setAppliedImsiFilter(imsiFilter);
          setAppliedTelefonFilter(telefonFilter);
          setAppliedDurumFilter(durumFilter);
          setAppliedBayiFilter(bayiFilter);
          setHasSearched(true);
          // Her zaman verileri getir (tarih seçilmişse o tarihe göre, "Tümü" seçildiyse tümü)
          // "all" seçildiğinde boş string gönder (tüm tarihler)
          fetchSims(selectedDate === "all" ? "" : (selectedDate || ""), true, {
            iccid: iccidFilter,
            imsi: imsiFilter,
            telefon: telefonFilter,
            bayi: bayiFilter,
          });
        }}
        searchButtonLoading={loading}
        searchButtonLoadingText="Listeleniyor..."
        onReset={() => {
          setSelectedDate("");
          setIccidFilter("");
          setImsiFilter("");
          setTelefonFilter("");
          setBayiFilter("");
          setDurumFilter("");
          setAppliedIccidFilter("");
          setAppliedImsiFilter("");
          setAppliedTelefonFilter("");
          setAppliedBayiFilter("");
          setAppliedDurumFilter("");
          setHasSearched(false);
          setSims([]);
        }}
        onKeyPress={(e) => {
          if (e.key === "Enter") {
            // Uygulanan filtreleri güncelle
            setAppliedIccidFilter(iccidFilter);
            setAppliedImsiFilter(imsiFilter);
            setAppliedTelefonFilter(telefonFilter);
            setAppliedDurumFilter(durumFilter);
            setAppliedBayiFilter(bayiFilter);
            setHasSearched(true);
            // Her zaman verileri getir (tarih seçilmişse o tarihe göre, seçilmemişse tümü)
            // "all" seçildiğinde boş string gönder (tüm tarihler)
            fetchSims(selectedDate === "all" ? "" : (selectedDate || ""), true, {
              iccid: iccidFilter,
              imsi: imsiFilter,
              telefon: telefonFilter,
              bayi: bayiFilter,
            });
          }
        }}
        actions={[
          {
            key: "bulk-upload",
            label: "Toplu Yükle",
            onClick: bulkDisclosure.onOpen,
            buttonProps: { colorScheme: "blue", variant: "outline", leftIcon: <FiUpload /> },
          },
          // SIM Operasyonları sadece admin ve saas_bayi için görünür
          ...(userRole === "admin" || (userRole === "bayi" && userBayiTipi === "saas_bayi") ? [{
            key: "operations",
            label: "SIM Operasyonları",
            onClick: handleOpenOperations,
            buttonProps: { 
              colorScheme: "blue", 
              variant: "outline", 
              leftIcon: <FiList />,
              isLoading: operationsLoading,
            },
          }] : []),
        ]}
      />

      <Box bg={panelBg} borderRadius="xl" p={{ base: 4, md: 6 }} border="1px solid" borderColor={panelBorder} mt={4}>
        <HStack justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">
            SIM Kartları
          </Text>
          <HStack spacing={4} align="center" flexWrap="wrap">
            {hasSearched && (
              <HStack spacing={3} fontSize="sm" color="gray.600">
                {appliedDurumFilter ? (
                  // Durum filtresi seçildiyse sadece o durumun sayısını göster
                  (() => {
                    let label = "";
                    let count = 0;
                    let color = "gray.600";
                    if (appliedDurumFilter === "hazir") {
                      label = "Hazır";
                      count = simStats.hazir;
                      color = "blue.600";
                    } else if (appliedDurumFilter === "kullanimda") {
                      label = "Kullanımda";
                      count = simStats.kullanimda;
                      color = "green.600";
                    } else if (appliedDurumFilter === "bosa_cikan") {
                      label = "Boşa Çıkan";
                      count = simStats.bosaCikan;
                      color = "orange.600";
                    }
                    if (label) {
                      return (
                        <Text fontWeight="medium">
                          <Text as="span" color={color} fontWeight="bold">{count}</Text> {label}
                        </Text>
                      );
                    }
                    return null;
                  })()
                ) : (
                  // Durum filtresi yoksa tüm istatistikleri göster
                  <>
                    <Text fontWeight="medium">
                      Toplam: <Text as="span" color="gray.800" fontWeight="bold">{simStats.total}</Text>
                    </Text>
                    <Text fontWeight="medium">
                      Kullanımda: <Text as="span" color="green.600" fontWeight="bold">{simStats.kullanimda}</Text>
                    </Text>
                    <Text fontWeight="medium">
                      Hazır: <Text as="span" color="blue.600" fontWeight="bold">{simStats.hazir}</Text>
                    </Text>
                    {simStats.bosaCikan > 0 && (
                      <Text fontWeight="medium">
                        Boşa Çıkan: <Text as="span" color="orange.600" fontWeight="bold">{simStats.bosaCikan}</Text>
                      </Text>
                    )}
                  </>
                )}
              </HStack>
            )}
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              leftIcon={<FiDownload />}
              onClick={handleDownloadPDF}
              isLoading={pdfLoading}
              loadingText="İndiriliyor..."
            >
              PDF İndir
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              variant="outline"
              leftIcon={<FiDownload />}
              onClick={handleDownloadExcel}
              isLoading={excelLoading}
              loadingText="İndiriliyor..."
            >
              Excel İndir
            </Button>
          </HStack>
        </HStack>
      {renderTable()}
        {renderPagination()}
      </Box>

      <SimContextMenu
        position={contextMenu.position}
        sim={contextMenu.sim}
        onClose={closeContextMenu}
        onToggleRoaming={handleToggleRoaming}
        onSetDurumHazir={handleSetDurumHazir}
        onShowAllNotes={handleShowAllNotes}
        onEditIdentifiers={handleEditIdentifiers}
      />

      <SimBulkUploadModal
        isOpen={bulkDisclosure.isOpen}
        onClose={bulkDisclosure.onClose}
        onSuccess={handleDataMutation}
        bayiler={bayiler} // ✅ Bayiler verisini prop olarak geç
        />

      <Modal isOpen={operationsDisclosure.isOpen} onClose={operationsDisclosure.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pr={{ base: 10, md: 12 }}>
            <HStack
              justify="space-between"
              align="center"
              gap={3}
              flexWrap="wrap"
            >
              <Text>SIM Operasyonları</Text>
              <HStack spacing={3} flexWrap="wrap" justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowCompletedOperations((prev) => !prev)}
                >
                  {completedToggleLabel}
                </Button>
                <Button
                  size="sm"
                  colorScheme="orange"
                  variant="outline"
                  leftIcon={<FiTrash2 />}
                  onClick={handleClearOldOperations}
                  isLoading={operationsLoading}
                >
                  Eski Operasyonları Sil
                </Button>
                {hasCancelableOperation && (
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<FiX />}
                  onClick={handleCancelAll}
                  isLoading={operationsLoading}
                >
                  Tümünü İptal Et
                </Button>
              )}
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {operationsLoading ? (
              <Center py={10}>
                <Spinner />
              </Center>
            ) : operationsError ? (
              <Center py={8}>
                <Text fontSize="sm" color="red.400">
                  {operationsError}
                </Text>
              </Center>
            ) : visibleOperations.length ? (
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Plaka</Th>
                      <Th>Cihaz ID</Th>
                      <Th>IMEI</Th>
                      <Th>Protokol</Th>
                      <Th>Durum</Th>
                      <Th>Güncelleme</Th>
                      <Th>İşlem</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleOperations.map((op) => (
                      <Tr key={op.id || `${op.device_id}-${op.updated_at}`}>
                        <Td>{op.plaka || "-"}</Td>
                        <Td>{op.device_id}</Td>
                        <Td>{op.imei || "-"}</Td>
                        <Td>{op.protocol ? op.protocol.toUpperCase() : "-"}</Td>
                        <Td>
                          <HStack spacing={2} align="center">
                            <Tooltip 
                              label={op.status ? op.status.replace(/⚠️|🔍|📊|⏰|🚀|✅|❌/g, "").trim() : getStatusLabel(op.status_code)} 
                              hasArrow
                              placement="top"
                            >
                              <Tag 
                                colorScheme={getStatusColor(op.status_code)}
                                size="sm"
                                cursor={op.status ? "help" : "default"}
                              >
                                {getStatusLabel(op.status_code)}
                              </Tag>
                            </Tooltip>
                            {op.warning && (
                              <Badge colorScheme="red" size="sm">{op.warning}</Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td>{op.updated_at || "-"}</Td>
                        <Td>
                          <HStack spacing={2} flexWrap="wrap">
                            {!op.completed && op.status_code !== "cancelled" && op.status_code !== "number_gathered" && op.status_code !== "timeout" && (
                              <>
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="outline"
                                  leftIcon={<FiRefreshCw />}
                                  onClick={() => handleCheckNow(op.device_id)}
                                  isLoading={checkingIds.has(op.device_id)}
                                >
                                  Şimdi Kontrol Et
                                </Button>
                                <Button
                                  size="xs"
                                  colorScheme="red"
                                  variant="outline"
                                  leftIcon={<FiX />}
                                  onClick={() => handleCancelOperation(op.device_id)}
                                  isLoading={cancellingIds.has(op.device_id)}
                                >
                                  İptal
                                </Button>
                              </>
                            )}
                            {/* Teltonika cihazlar için bilgi alma komutu - sadece işlem tamamlanmamışsa göster */}
                            {op.protocol && 
                             op.protocol.toLowerCase() === "teltonika" && 
                             !op.completed && 
                             op.status_code !== "number_gathered" && (
                              <Button
                                size="xs"
                                colorScheme="teal"
                                variant="outline"
                                onClick={() => handleSendTeltonikaInfo(op.device_id)}
                                isLoading={sendingInfoIds.has(op.device_id)}
                                title="Cihaza bilgi alma komutu (getinfo) gönder"
                              >
                                Komut Gönder
                              </Button>
                            )}
                            {/* GT06/GS900: ICCID komutu — simInfo dolar; ham log kullanılmaz */}
                            {op.protocol &&
                             ["gt06", "gs900"].includes(op.protocol.toLowerCase()) &&
                             !op.completed &&
                             op.status_code !== "number_gathered" && (
                              <Button
                                size="xs"
                                colorScheme="teal"
                                variant="outline"
                                onClick={() => handleSendGt06Iccid(op.device_id)}
                                isLoading={sendingInfoIds.has(op.device_id)}
                                title="Traccar üzerinden ICCID komutu gönder"
                              >
                                Komut Gönder (ICCID)
                              </Button>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : operations.length ? (
              <Center py={8}>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  Aktif SIM operasyonu bulunmuyor.
                  <br />
                  Tamamlanan işlemleri görmek için üstteki butonu kullanın.
                </Text>
              </Center>
            ) : (
              <Center py={8}>
                <Text fontSize="sm" color="gray.500">
                  Aktif SIM operasyonu bulunmuyor.
                </Text>
              </Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={refreshOperations}
              leftIcon={<FiRefreshCw />}
              isLoading={operationsLoading}
            >
              Yenile
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SimNotlarModal
        sim={notesModalSim}
        isOpen={Boolean(notesModalSim)}
        onClose={() => setNotesModalSim(null)}
      />

      <SimIdentifiersEditModal
        sim={editIdentifiersSim}
        isOpen={Boolean(editIdentifiersSim)}
        onClose={() => setEditIdentifiersSim(null)}
        onSaved={(updated) => {
          setSims((prev) =>
            prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
          );
        }}
      />
    </Box>
    </>
  );
}
